/* qrCard: a downloaded QR is a thing that gets PRINTED — taped to a table,
 * propped by the till — so instead of a bare code on white it leaves here as
 * a small poster: the restaurant's name up top in the app's display face,
 * what the code opens under it, the code itself on a shadowed white panel
 * with scanner brackets and a SCAN ME pill, and the branch in the footer.
 * Everything is drawn on a canvas at print-friendly size (1000×1340).
 */

const W = 1000;
const H = 1340;

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/* Shrink until the line fits — a long restaurant or menu name must never clip. */
const fitFont = (ctx, text, maxWidth, px, family, weight) => {
  let size = px;
  while (size > 20) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (ctx.measureText(text).width <= maxWidth) break;
    size -= 2;
  }
  return size;
};

/* The app's own display face, read off the font tokens so the card matches
   the product; Georgia stands in when the token is empty or not loaded. */
const displayFamily = () => {
  const v = (getComputedStyle(document.documentElement)
    .getPropertyValue('--font-display') || '').trim();
  return v || "Georgia, 'Times New Roman', serif";
};
const SANS = "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif";

export async function downloadQrCard({
  qrDataUrl, title, subtitle, hint, hint2, footnote, fileName,
}) {
  try { await document.fonts?.ready; } catch { /* draw with fallbacks */ }
  const img = await loadImage(qrDataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  const AMBER = '#b45309';
  const AMBER_BRIGHT = '#d97706';
  const INK = '#292524';
  const DISPLAY = displayFamily();

  // Warm paper with two soft amber pools, so even the background isn't flat.
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#fffdf8');
  bg.addColorStop(1, '#faf1e0');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);
  const pool = (x, y, r) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, 'rgba(217, 119, 6, 0.10)');
    g.addColorStop(1, 'rgba(217, 119, 6, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  };
  pool(W - 110, 120, 330);
  pool(90, H - 140, 300);

  // A double frame, like a menu cover: solid outside, hairline inside.
  ctx.strokeStyle = AMBER_BRIGHT;
  ctx.lineWidth = 3;
  rr(ctx, 26, 26, W - 52, H - 52, 26);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(180, 83, 9, 0.35)';
  ctx.lineWidth = 1;
  rr(ctx, 40, 40, W - 80, H - 80, 18);
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Ornament: rules meeting a diamond, the classic menu-card divider.
  const orn = (y, half) => {
    ctx.strokeStyle = AMBER_BRIGHT;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(W / 2 - half, y); ctx.lineTo(W / 2 - 26, y);
    ctx.moveTo(W / 2 + 26, y); ctx.lineTo(W / 2 + half, y);
    ctx.stroke();
    ctx.save();
    ctx.translate(W / 2, y);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = AMBER_BRIGHT;
    ctx.fillRect(-7, -7, 14, 14);
    ctx.restore();
  };
  orn(118, 150);

  // The restaurant, as large as its name allows.
  ctx.fillStyle = INK;
  const titleSize = fitFont(ctx, title, W - 180, 76, DISPLAY, '700');
  ctx.font = `700 ${titleSize}px ${DISPLAY}`;
  ctx.fillText(title, W / 2, 205);

  // What this code opens, letter-spaced small caps under the name.
  const sub = String(subtitle || '').toUpperCase().split('').join('  ');
  ctx.fillStyle = AMBER;
  const subSize = fitFont(ctx, sub, W - 200, 34, SANS, '700');
  ctx.font = `700 ${subSize}px ${SANS}`;
  ctx.fillText(sub, W / 2, 205 + titleSize * 0.75 + 20);

  // The QR on its own white panel, lifted by a soft shadow.
  const panel = 620;
  const px = (W - panel) / 2;
  const py = 380;
  ctx.save();
  ctx.shadowColor = 'rgba(41, 37, 36, 0.16)';
  ctx.shadowBlur = 36;
  ctx.shadowOffsetY = 14;
  ctx.fillStyle = '#ffffff';
  rr(ctx, px, py, panel, panel, 30);
  ctx.fill();
  ctx.restore();

  const qsize = 520;
  ctx.drawImage(img, (W - qsize) / 2, py + (panel - qsize) / 2, qsize, qsize);

  // Scanner brackets just outside the panel corners — "aim here".
  const bl = 46;
  const gap = 16;
  ctx.strokeStyle = AMBER_BRIGHT;
  ctx.lineWidth = 6;
  ctx.lineCap = 'round';
  [[px - gap, py - gap, 1, 1], [px + panel + gap, py - gap, -1, 1],
   [px - gap, py + panel + gap, 1, -1], [px + panel + gap, py + panel + gap, -1, -1],
  ].forEach(([cx, cy, sx, sy]) => {
    ctx.beginPath();
    ctx.moveTo(cx + sx * bl, cy);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx, cy + sy * bl);
    ctx.stroke();
  });

  // The SCAN ME pill riding the panel's top edge.
  ctx.font = `700 24px ${SANS}`;
  const pillText = 'S C A N   M E';
  const pw = ctx.measureText(pillText).width + 64;
  ctx.fillStyle = AMBER_BRIGHT;
  rr(ctx, (W - pw) / 2, py - 26, pw, 52, 26);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(pillText, W / 2, py + 1);

  // How, then why — the two hint lines under the code.
  ctx.fillStyle = INK;
  ctx.font = `600 30px ${SANS}`;
  ctx.fillText(hint || 'Point your phone camera at the code', W / 2, py + panel + 84);
  if (hint2) {
    ctx.fillStyle = '#78716c';
    ctx.font = `400 25px ${SANS}`;
    ctx.fillText(hint2, W / 2, py + panel + 126);
  }

  orn(H - 156, 110);

  // The branch, then the maker, quietly at the foot.
  if (footnote) {
    ctx.fillStyle = '#57534e';
    ctx.font = `600 24px ${SANS}`;
    ctx.fillText(footnote, W / 2, H - 112);
  }
  ctx.fillStyle = '#a8a29e';
  ctx.font = `400 20px ${SANS}`;
  ctx.fillText('Powered by RestoBOXX', W / 2, H - 70);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'qr-card.png';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
